import {CanDeactivateFn} from '@angular/router';
import {inject} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {map} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {HasUnsavedChanges} from './unsaved-changes.interface';
import {ConfirmDialogComponent} from '../corporate/confirm-dialog/confirm-dialog.component';

export const unsavedChangesGuard: CanDeactivateFn<HasUnsavedChanges> = (
  component,
  currentRoute,
  currentState,
  nextState
) => {
  const dialog = inject(MatDialog);

  if (!component || typeof component.hasUnsavedChanges !== 'function') {
    return true;
  }

  if (!component.hasUnsavedChanges()) {
    return true;
  }

  const destination = extractDestinationName(nextState?.url);

  const dialogRef = dialog.open(ConfirmDialogComponent, {
    width: 'min(450px, 92vw)',
    maxWidth: '92vw',
    panelClass: 'confirm-dialog-container',
    disableClose: true,
    data: {
      title: 'Unsaved Changes',
      text: `You may have unsaved changes. Please check before you move to ${destination}.`,
      subText: 'Leave Page'
    }
  });

  return dialogRef.afterClosed().pipe(
    map(result => result === true)
  );
};

function extractDestinationName(url?: string): string {
  if (!url) return 'another page';

  const cleanUrl = url.split('?')[0].replace(/^\//, '');

  const routeNameMap: Record<string, string> = {
    '': 'Home',
    'forms-table': 'Forms Table',
    'settings/user': 'User Settings',
    'settings/organization': 'Organization Settings',
    'invoicing-dashboard': 'Invoicing Dashboard',
    'pluton-invoicing-dashboard': 'Invoicing Dashboard',
    'time-sheet': 'Time Sheet',
    'important-dates': 'Important Dates',
    'reports': 'Reports',
    'audit-logs': 'Audit Logs',
    'file-system': 'File System',
    'notifications': 'Notifications',
    'task-planner': 'Task Planner',
    'company-shares': 'Company Shares',
    'reference-numbers': 'Reference Numbers',
  };

  if (routeNameMap[cleanUrl] !== undefined) {
    return routeNameMap[cleanUrl];
  }

  const lastSegment = cleanUrl.split('/').pop() || 'another page';
  return lastSegment
    .replace(/-/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}
